import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  BellRing,
  CheckCircle2,
  ClipboardList,
  DollarSign,
  History,
  Package,
  RefreshCw,
  RotateCcw,
  ShoppingCart,
  Truck,
} from "lucide-react";

import EbayChannelLogo from "../../assests/channel-ebay-generated.png";
import FacebookMarketplaceLogo from "../../assests/channel-facebook-marketplace-generated.png";
import WalmartChannelLogo from "../../assests/channel-walmart-generated.png";
import BobbinLogo from "../../assests/bobbin.png";
import { api } from "../../services/api";
import { StatusPill } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { ToastStack, useToast } from "./useToast";

const channelLogos = {
  ebay: EbayChannelLogo,
  facebook_marketplace: FacebookMarketplaceLogo,
  walmart: WalmartChannelLogo,
  bobbinusa: BobbinLogo,
};

const emptyOverview = {
  metrics: {
    products: 0,
    activeProducts: 0,
    lowStock: 0,
    orders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    shippingQueue: 0,
    pendingReturns: 0,
    openNotifications: 0,
    revenue: 0,
    revenue30d: 0,
    orders30d: 0,
    connectedChannels: 0,
    failedSyncJobs: 0,
  },
  channels: [],
  activity: [],
  recentOrders: [],
  lowStockProducts: [],
};

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(date) {
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/New_York",
  }).format(new Date(date));
}

function channelLabel(channel = {}) {
  const labels = {
    bobbinusa: "Bobbin USA",
    direct: "Direct",
    ebay: "eBay",
    facebook_marketplace: "Facebook Marketplace",
    walmart: "Walmart",
  };
  return labels[channel.name] || channel.displayName || channel.name || "Channel";
}

function statusTone(status) {
  if (["connected", "paid", "shipped", "delivered", "success", "active"].includes(status)) return "good";
  if (["error", "failed", "cancelled", "refunded"].includes(status)) return "bad";
  if (["pending", "processing", "syncing", "unpaid", "inspection", "refund_due"].includes(status)) return "warn";
  return "neutral";
}

function MetricCard({ icon: Icon, label, value, note, tone = "blue", onClick }) {
  const toneClasses = {
    blue: "bg-blue-50 text-[#4391d1]",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <button
      className="rounded-2xl border border-gray-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-100 hover:shadow-md"
      onClick={onClick}
      type="button"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-gray-500">{label}</p>
          <p className="mt-3 text-3xl font-black text-gray-900">{value}</p>
        </div>
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${toneClasses[tone] || toneClasses.blue}`}>
          <Icon size={20} />
        </span>
      </div>
      <p className="mt-3 text-xs font-semibold text-gray-400">{note}</p>
    </button>
  );
}

function EmptyBlock({ title, note }) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-8 text-center">
      <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
      <h3 className="mt-3 text-sm font-black text-gray-900">{title}</h3>
      <p className="mt-1 text-xs font-semibold text-gray-500">{note}</p>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="grid gap-4">
      {[1, 2, 3].map((item) => (
        <div className="animate-pulse rounded-2xl bg-white p-5" key={item}>
          <div className="h-4 w-2/3 rounded bg-gray-100" />
          <div className="mt-3 h-3 w-1/3 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { dismiss, toast, toasts } = useToast();
  const loadOverview = useCallback(async () => api.get("/admin/overview"), []);
  const { data = emptyOverview, error, loading, reload } = useAdminResource(loadOverview, emptyOverview);
  const metrics = { ...emptyOverview.metrics, ...(data.metrics || {}) };
  const channels = data.channels || [];
  const recentOrders = data.recentOrders || [];
  const lowStockProducts = data.lowStockProducts || [];
  const activity = data.activity || [];

  useEffect(() => {
    if (error && !loading) {
      toast(`Dashboard data unavailable: ${error}`, "warning");
    }
  }, [error, loading, toast]);

  const actionQueue = [
    {
      label: "Orders to process",
      value: metrics.pendingOrders,
      note: "Pending, paid, or processing",
      icon: ClipboardList,
      tone: metrics.pendingOrders ? "amber" : "green",
      href: "/dashboard/orders",
    },
    {
      label: "Shipments needed",
      value: metrics.shippingQueue,
      note: "Paid or processing without tracking",
      icon: Truck,
      tone: metrics.shippingQueue ? "amber" : "green",
      href: "/dashboard/orders",
    },
    {
      label: "Returns needing action",
      value: metrics.pendingReturns,
      note: "Pending, inspection, or refund due",
      icon: RotateCcw,
      tone: metrics.pendingReturns ? "amber" : "green",
      href: "/dashboard/returns",
    },
    {
      label: "Open alerts",
      value: metrics.openNotifications,
      note: "Inventory, sync, order, and system alerts",
      icon: BellRing,
      tone: metrics.openNotifications ? "red" : "green",
      href: "/dashboard/notifications",
    },
  ];

  return (
    <div className="space-y-6 p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Dashboard</p>
            <h1 className="mt-2 text-3xl font-black text-gray-900">Operations overview</h1>
            <p className="mt-2 max-w-3xl text-sm font-semibold text-gray-500">
              Real-time view of catalog health, orders, shipping, returns, marketplace channels, and recent admin activity.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
              onClick={() => navigate("/dashboard/products/new")}
              type="button"
            >
              <Package size={16} />
              Add Product
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={reload}
              type="button"
            >
              <RefreshCw className={loading ? "animate-spin" : ""} size={16} />
              Refresh
            </button>
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={DollarSign} label="Paid Revenue" note={`${metrics.orders30d} paid orders in last 30 days`} onClick={() => navigate("/dashboard/reports")} tone="green" value={formatCurrency(metrics.revenue)} />
        <MetricCard icon={ShoppingCart} label="Total Orders" note={`${metrics.paidOrders} paid orders recorded`} onClick={() => navigate("/dashboard/orders")} value={metrics.orders} />
        <MetricCard icon={Package} label="Products" note={`${metrics.activeProducts} active products`} onClick={() => navigate("/dashboard/products")} value={metrics.products} />
        <MetricCard icon={AlertTriangle} label="Low Stock" note="Products at or below threshold" onClick={() => navigate("/dashboard/products")} tone={metrics.lowStock ? "amber" : "green"} value={metrics.lowStock} />
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {actionQueue.map((item) => (
          <MetricCard
            icon={item.icon}
            key={item.label}
            label={item.label}
            note={item.note}
            onClick={() => navigate(item.href)}
            tone={item.tone}
            value={item.value}
          />
        ))}
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-black text-gray-900">Channel health</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">{metrics.connectedChannels} connected, {metrics.failedSyncJobs} failed sync jobs</p>
            </div>
            <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={() => navigate("/dashboard/sync")} type="button">
              Manage Channels
              <ArrowUpRight size={15} />
            </button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-1">
            {channels.length ? (
              channels.map((channel) => (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-3 py-2" key={channel._id || channel.name}>
                  <div className="flex min-h-[62px] items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${channel.status === "connected" ? "bg-emerald-500" : channel.status === "error" ? "bg-red-500" : "bg-gray-300"}`} />
                      {channelLogos[channel.name] ? (
                        <img alt={channelLabel(channel)} className="h-12 w-full max-w-[190px] object-contain" src={channelLogos[channel.name]} />
                      ) : (
                        <span className="rounded-xl bg-white px-3 py-2 text-sm font-black text-gray-900 shadow-sm">
                          {channelLabel(channel)}
                        </span>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <StatusPill tone={statusTone(channel.status)}>{channel.status}</StatusPill>
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                        {formatDate(channel.lastSyncedAt || channel.lastSyncAt || channel.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyBlock title="No channels configured" note="Connected channels will appear here." />
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-gray-900">Low stock products</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">Sorted by lowest stock</p>
            </div>
            <button className="text-sm font-black text-[#4391d1]" onClick={() => navigate("/dashboard/products")} type="button">View all</button>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <LoadingBlock />
            ) : lowStockProducts.length ? (
              lowStockProducts.map((product) => (
                <button className="flex w-full items-center justify-between gap-4 rounded-2xl bg-gray-50 px-4 py-3 text-left hover:bg-blue-50" key={product._id} onClick={() => navigate(`/dashboard/products/${product._id}/edit`)} type="button">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-gray-900">{product.name}</span>
                    <span className="mt-1 block text-xs font-semibold text-gray-500">{product.sku || "No SKU"}</span>
                  </span>
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
                    {product.stock || 0}/{product.lowStockThreshold || 0}
                  </span>
                </button>
              ))
            ) : (
              <EmptyBlock title="Stock looks healthy" note="No products are below their threshold." />
            )}
          </div>
        </section>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-gray-900">Recent orders</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">Latest orders from all channels</p>
            </div>
            <button className="text-sm font-black text-[#4391d1]" onClick={() => navigate("/dashboard/orders")} type="button">View orders</button>
          </div>
          <div className="mt-5 space-y-3">
            {recentOrders.length ? (
              recentOrders.map((order) => (
                <button className="flex w-full items-center justify-between gap-4 rounded-2xl bg-gray-50 px-4 py-3 text-left hover:bg-blue-50" key={order._id} onClick={() => navigate(`/dashboard/orders/${order._id}`)} type="button">
                  <span>
                    <span className="block text-sm font-black text-gray-900">{order.orderNumber}</span>
                    <span className="mt-1 block text-xs font-semibold text-gray-500">{channelLabel({ name: order.channel })} - {formatDate(order.createdAt)}</span>
                  </span>
                  <span className="flex flex-col items-end gap-1">
                    <span className="text-sm font-black text-gray-900">{formatCurrency(order.total)}</span>
                    <StatusPill tone={statusTone(order.status)}>{order.status}</StatusPill>
                  </span>
                </button>
              ))
            ) : (
              <EmptyBlock title="No orders yet" note="New orders will appear here." />
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-gray-900">Recent activity</h2>
              <p className="mt-1 text-sm font-semibold text-gray-500">Latest admin and system events</p>
            </div>
            <button className="inline-flex items-center gap-1 text-sm font-black text-[#4391d1]" onClick={() => navigate("/dashboard/activity")} type="button">
              View logs
              <History size={15} />
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {activity.length ? (
              activity.slice(0, 6).map((log) => (
                <div className="rounded-2xl bg-gray-50 px-4 py-3" key={log._id}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-gray-900">{log.action}</p>
                      <p className="mt-1 text-xs font-semibold text-gray-500">{log.module} - {log.actorEmail || "System"}</p>
                    </div>
                    <StatusPill tone={statusTone(log.status)}>{log.status || "success"}</StatusPill>
                  </div>
                </div>
              ))
            ) : (
              <EmptyBlock title="No recent activity" note="Admin and system actions will appear here." />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
